import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenav } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, switchMap } from 'rxjs';

import { buildTimestamp } from '@shared-build-timestamp';

import { environment } from '../environments/environment';
import { IMatCustomSvgIcons } from './interfaces/custom-material-icons';
import { IApproverList } from './scripts/approvers';
import { ApiService } from './services/api.service';
import { DatahubService, IApplicationQueryParams } from './services/datahub.service';
import { ModalService } from './services/modal.service';

interface INavbarItem {
  display: string;
  key: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  standalone: false,
})
export class AppComponent implements OnInit {
  public readonly buildTimestamp = buildTimestamp;
  public readonly envName = environment.environmentNameFull;
  public readonly envCssClass = `env-${environment.environmentName.toLowerCase()}`;
  public readonly isProd = environment.production;
  public readonly isDev = environment.development;
  public readonly torUrl = environment.torUrl;
  public readonly foremanAppUrl = `${environment.webServerUrl}foreman`;
  public readonly navbar: INavbarItem[] = [
    { display: 'Home', key: 'home' },
    { display: 'Payroll', key: 'payroll' },
    { display: 'Billing', key: 'billing' },
    { display: 'Timesheets', key: 'timesheetmanager' },
  ];

  public get userAuthorized$() {
    return this.datahub.user.authorized$;
  }
  public get ready$() {
    return this.datahub.ready$;
  }
  public get userName() {
    return this.datahub.user.userName;
  }
  public get approvingName() {
    return this.datahub.user.approvingName;
  }
  public get isWorkingAsProxyApprover(): boolean {
    return this.datahub.user.getEmpId() !== this.datahub.user.approvingId;
  }
  public get unauthorizedErrorMessage() {
    return this.datahub.user.unauthorizedErrorMessage;
  }
  public get unauthorizedErrorEmailHref() {
    return `mailto:DaveyTimeSupport@davey.com?subject=Issue Logging In To The Davey Time General Foreman App&body=${encodeURIComponent(this.datahub.user.unauthorizedErrorEmailBody)}`;
  }
  public get landingPageUrl(): string {
    return environment.landingPageUrl;
  }

  public get buildDeploymentTitle(): string {
    return `This was deployed on ${this.buildTimestamp.full}`;
  }

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly apiService: ApiService,
    private readonly datahub: DatahubService,
    private readonly modalService: ModalService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly domSanitizer: DomSanitizer,
    private readonly matIconRegistry: MatIconRegistry,
  ) {
    this.datahub.navigator$.subscribe({
      next: (url: string) => {
        void router.navigateByUrl(url);
      },
      error: (err) => {
        console.error('Navigation error:', err);
      },
    });

    this.datahub.params$.subscribe({
      next: (params) => {
        void this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: params,
        });
      },
      error: (err) => {
        console.error('Params error:', err);
      },
    });

    this.registerCustomMatIcons();
  }

  public ngOnInit(): void {
    if (!this.isProd) {
      //non-prod environments get the favicon swapped out to match the environment color
      const favicon = this.document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (favicon) {
        favicon.href = favicon.href.replace('.png', `-${environment.environmentName.toLowerCase()}.png`);
      }
    }
  }

  public easyTicket() {
    const messageSeed = `I am unable to access the Davey Time application.\nEmployee Id: ${this.datahub.user.employeeId}\n\nPlease investigate this issue and contact me when it is resolved.`;

    this.modalService.launchResponsiveModal(
      'need-help',
      {
        messageSeed,
        emailAddressOverride: 'daveytimesupport@davey.com',
        subjectOverride: `Davey Time General Foreman App Access Issue`,
      },
      { autoFocus: 'first-tabbable' },
    );
  }

  public checkRoute(key: INavbarItem): boolean {
    const url = window.location.href;
    return url.search(key.key) > -1;
  }

  public get currentQueryParams(): IApplicationQueryParams {
    return {
      approver: this.datahub.user.approvingId,
      wedate: this.datahub.weDate.getSapWeDateString(),
    };
  }

  public needHelp(drawer?: MatSidenav): void {
    if (drawer !== undefined) void drawer.close();
    this.modalService.launchResponsiveModal('need-help', null, { autoFocus: 'first-tabbable' });
  }

  public about(drawer: MatSidenav): void {
    void drawer.close();
    this.modalService.launchResponsiveModal('about', null, { width: '630px' });
  }

  public signOut(): void {
    this.apiService.signOut$().subscribe({
      error: (err) => {
        console.error('Error signing out:', err);
      },
    });
  }
  public switchUser(): void {
    this.modalService
      .launchResponsiveModal('approver-select', { approvers: this.datahub.approvers.approvers }, { autoFocus: 'first-tabbable' })
      .afterClosed()
      .pipe(
        filter((collection: IApproverList) => !!collection),
        switchMap((collection: IApproverList) => this.datahub.user.switchApprover$(collection.value)),
      )
      .subscribe({
        next: (approver) => {
          this.datahub.switchApprover(approver);
        },
        error: (err) => {
          console.error('Error switching approver:', err);
        },
      });
  }

  private registerCustomMatIcons(): void {
    const customSvgIcons: IMatCustomSvgIcons[] = ['davey-time-logo', 'move-time', 'map-with-pin', 'help'];

    customSvgIcons.forEach((icon) => {
      this.matIconRegistry.addSvgIcon(
        icon,
        this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/custom-mat-icons/${icon}.svg`),
      );
    });
  }
}
